import { Divider, Stack } from '@mui/material';
import { Button, DatePickerInput, GenericDropdown, Label, ListGroupHeader } from 'customer_portal-ui-shared';
import { MaterialReactTable, MRT_ColumnDef, useMaterialReactTable } from 'material-react-table';
import { useEffect, useMemo, useState } from 'react';
import { AbsColor } from '../../constants';
import { IReportBenchmark_Port } from '../../contracts/report-benchmark.contract';
import { BenchmarkFetch } from '../../features/benchmark.feature';
import { useAppDispatch, useAppSelector } from '../../hooks';
import Loading from '../loading.component';

const denormalizeJson = (data: any, parentKey: string = '', result: any[] = [{}]): any[] => {
    for (let key in data) {
        if (data.hasOwnProperty(key)) {
            const newKey = parentKey ? `${parentKey}${key.charAt(0).toUpperCase() + key.slice(1)}` : key;
            if (Array.isArray(data[key])) {
                const tempResult: any = [];
                data[key].forEach((item: any, index: any) => {
                    const denormalizedItem = denormalizeJson(item, `${newKey}`, [{}]);
                    denormalizedItem.forEach((flatItem) => {
                        tempResult.push({ ...result[0], ...flatItem });
                    });
                });
                result = tempResult;
            } else if (typeof data[key] === 'object' && data[key] !== null) {
                result = denormalizeJson(data[key], `${newKey}`, result);
            } else {
                result.forEach((r) => {
                    r[newKey] = data[key];
                });
            }
        }
    }
    return result;
};

const BenchmarkFleet = () => {
    const dispatch = useAppDispatch();

    const reportData = useAppSelector((state) => state.Benchmark.List.Result);
    const isLoadingReport = useAppSelector((state) => state.Benchmark.List.IsLoading);
    const vesselTypes = useAppSelector((state) => state.Benchmark.VesseTypes);
    const mous = useAppSelector((state) => state.Benchmark.MOUs);
    const ports = useAppSelector((state) => state.Benchmark.Ports);

    const [startDate, setStartDate] = useState<Date | null>(new Date());
    const [endDate, setEndDate] = useState<Date | null>(new Date());

    const [selectedVesselTypes, setSelectedVesselTypes] = useState<string[]>([]);
    const [selectedMOUs, setSelectedMOUs] = useState<string[]>([]);
    const [selectedPorts, setSelectedPorts] = useState<string[]>([]);
    const [selectedAge, setSelectedAge] = useState<string>();

    const [flatData, setFlatDate] = useState<any[]>([]);

    useEffect(() => {
        if (!reportData) return;

        const flatReportData = reportData.flatMap((item) => denormalizeJson(item));
        setFlatDate(flatReportData);
    }, [reportData]);

    useEffect(() => {
        dispatch(BenchmarkFetch.GetVesselTypes());
        dispatch(BenchmarkFetch.GetMOUs());
        dispatch(BenchmarkFetch.GetPorts());
    }, []);

    const onClickGenerate = async () => {
        await loadReport();
    };

    const loadReport = async () => {
        dispatch(BenchmarkFetch.GetReport({ 
        startDate: new Date('2020-01-01'), 
        endDate: new Date('2025-01-01'), 
        moUs: selectedMOUs,
        vesselTypes: selectedVesselTypes,
        ports: selectedPorts}));
    };

    const columns = useMemo<MRT_ColumnDef<any>[]>(
        () => [
            {
                accessorKey: 'MOU',
                header: 'MOU',
                size: 10
            },
            {
                accessorFn: (row) => `${row.DeficiencyCodesDeficiencyCode} - ${row.DeficiencyCodesDeficiencyDescription}`,
                id: 'DeficiencyCode',
                header: 'Deficiency Code',
                size: 10
            },
            {
                accessorFn: (row) => `${row.DeficiencyCodesVesselsVesselName} (${row.DeficiencyCodesVesselsIMONumber})`,
                id: 'Vessel',
                header: 'Vessel',
                size: 10
            },
            {
                accessorKey: 'DeficiencyCodesVesselsDeficiencyCodeCount',
                header: 'Count',
                size: 10
            }
        ],
        []
    );

    const table = useMaterialReactTable({
        columns,
        data: flatData ?? [],
        enableGrouping: true,
        enablePagination: false,
        initialState: {
            grouping: ['MOU', 'DeficiencyCode']
        },
        muiTableHeadCellProps: {
            sx: {
                backgroundColor: AbsColor.Blue1,
                color: AbsColor.White1,
                '& .MuiSvgIcon-root': {
                    color: AbsColor.White1 // Change the color of the icons
                },
                '& .MuiTableSortLabel-icon': {
                    color: AbsColor.White1 // Change the color of the sorting icons
                }
            }
        },
        muiTableBodyRowProps: ({ row }) => ({
            sx: {
                backgroundColor: row.index % 2 === 0 ? AbsColor.White1 : AbsColor.Gray1 // Zebra striping
            }
        })
    });

    return (
        <Stack direction={'column'} gap={2}>
            <ListGroupHeader title="My Fleet" />

            <Stack direction={'row'} justifyContent={'flex-start'} spacing={2}>
                <Stack direction={'column'} style={{ width: '25%' }}>
                    <Label name="Start Date" labelColor={AbsColor.Blue1} size="large" bold />
                    <DatePickerInput onChange={setStartDate} value={startDate} />
                </Stack>

                <Stack direction={'column'} style={{ width: '25%' }}>
                    <Label name="End Date" labelColor={AbsColor.Blue1} size="large" bold />
                    <DatePickerInput onChange={setEndDate} value={endDate} />
                </Stack>
            </Stack>

            <Stack direction={'row'} justifyContent={'space-between'} spacing={2}>
                <Stack direction={'column'} style={{ width: '25%' }}>
                    <Label name="Vessel Type" labelColor={AbsColor.Blue1} size="large" bold />
                    <GenericDropdown
                        options={vesselTypes?.map((i) => {
                            return { label: i, value: i };
                        })}
                        selected={selectedVesselTypes}
                        handleChange={(i: any) => setSelectedVesselTypes(i)}
                        placeholder="Select Options"
                        multiple
                        showResetSelectionButton
                    />
                </Stack>
                <Stack direction={'column'} style={{ width: '25%' }}>
                    <Label name="MOU" labelColor={AbsColor.Blue1} size="large" bold />
                    <GenericDropdown
                        options={mous?.map((i) => {
                            return { label: i, value: i };
                        })}
                        selected={selectedMOUs}
                        handleChange={(i: any) => setSelectedMOUs(i)}
                        placeholder="Select Options"
                        multiple
                        showResetSelectionButton
                    />
                </Stack>
                <Stack direction={'column'} style={{ width: '25%' }}>
                    <Label name="Port" labelColor={AbsColor.Blue1} size="large" bold />
                    <GenericDropdown
                        options={ports?.map((i) => {
                            return { label: `${i.Name}, ${i.Authority}`, value: i.Name };
                        })}
                        selected={selectedPorts}
                        handleChange={(i: any) => setSelectedPorts(i)}
                        placeholder="Select Options"
                        multiple
                        showResetSelectionButton
                    />
                </Stack>
                <Stack direction={'column'} style={{ width: '25%' }}>
                    <Label name="Age" labelColor={AbsColor.Blue1} size="large" bold />
                    <GenericDropdown
                        options={[
                            { label: 'Under 50 Years Old', value: 'Under 50 Years Old' },
                            { label: 'Over 50 Years Old', value: 'Over 50 Years Old' }
                        ]}
                        selected={selectedAge}
                        handleChange={(i: any) => setSelectedAge(i)}
                        placeholder="Select Options"
                    />
                </Stack>
            </Stack>

            {!isLoadingReport && (
                <Stack direction={'row'} justifyContent={'flex-end'} spacing={2}>
                    <Button onClick={onClickGenerate}>Generate</Button>
                </Stack>
            )}

            <Divider />

            {isLoadingReport ? (
                <Stack direction={'row'} justifyContent={'center'} spacing={2}>
                    <Loading />
                </Stack>
            ) : (
                <>
                    <Stack style={{ zIndex: 0 }}>{reportData && <MaterialReactTable table={table} />}</Stack>
                </>
            )}
        </Stack>
    );
};

export default BenchmarkFleet;
